import "./ModalConfirmSettlement.scss";

import * as formik from "formik";
import * as yup from "yup";

import { Form, Modal } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";

import AppButton from "../../commons/buttons/AppButton";
import AppContainer from "../../commons/containers/AppContainer";
import AppLinkButton from "../../commons/buttons/AppLinkButton";
import AppLoading from "../../commons/AppLoading";
import AppTextAreaV2 from "../../commons/inputs/AppTextAreaV2";
import CalendarInputV2 from "../../commons/inputs/CalendarInputV2";
import { formatDateInEN } from "../../commons/util/HallAppUtil";

export default function ModalConfirmSettlement({ show, closeShow, transaction, onConfirm }) {
  const { Formik } = formik;

  const [isLoading, setLoading] = useState(false);

  // Helpers
  const parseYMDToDate = (ymd) => {
    if (!ymd) return null;
    const parts = String(ymd).split("-");
    if (parts.length !== 3) return null;

    const [y, m, d] = parts.map((p) => Number(p));
    if (!y || !m || !d) return null;

    const dt = new Date(y, m - 1, d);
    dt.setHours(0, 0, 0, 0);
    return dt;
  };

  const isEntry = transaction?.type === "INPUT";
  const title = isEntry ? "CONFIRMAR RECEBIMENTO" : "CONFIRMAR PAGAMENTO";

  // ✅ Data inicial do modal: paymentDate se existir, senão hoje
  const initialDateObj = useMemo(() => {
    const fromPayment = parseYMDToDate(transaction?.paymentDate);
    if (fromPayment) return fromPayment;

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, [transaction?.paymentDate]);

  // ✅ StartDate é o Date que o CalendarInputV2 usa
  const [startDate, setStartDate] = useState(initialDateObj);

  // ✅ dateValue é o "yyyy-MM-dd" que você envia no payload
  const [dateValue, setDateValue] = useState(formatDateInEN(initialDateObj));

  // ✅ Form inicial (observação): paymentObservation se existir, senão vazio
  const initialValues = useMemo(
    () => ({
      description: transaction?.paymentObservation || "",
    }),
    [transaction?.paymentObservation]
  );

  // Schema
  const schema = yup.object().shape({
    description: yup.string().max(500, "Máximo 500 caracteres").nullable(),
  });

  // ✅ Sempre que abrir, ou trocar transação, reconfigura campos
  useEffect(() => {
    if (!show) return;

    setStartDate(initialDateObj);
    setDateValue(formatDateInEN(initialDateObj));
  }, [show, initialDateObj]);

  const handleSubmitConfirm = async (values, { resetForm }) => {
    try {
      setLoading(true);

      const payload = {
        paymentDate: dateValue || formatDateInEN(new Date()),
        paymentObservation: values?.description || "",
      };

      await onConfirm?.(transaction?.id, payload);

      resetForm();
      closeShow?.();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} fullscreen onHide={closeShow}>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 15 }}>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <AppContainer>
          <Formik
            validationSchema={schema}
            initialValues={initialValues}
            onSubmit={handleSubmitConfirm}
            enableReinitialize
          >
            {({ handleSubmit, handleChange, values, errors, touched }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <div className="mb-2 section-title">
                  {isEntry ? "Data do recebimento" : "Data do pagamento"}
                </div>

                <div className="section-subtitle mb-3">
                  {transaction?.paymentDate
                    ? "Este lançamento já foi confirmado. Você pode ajustar a data e a observação."
                    : "Por padrão, usamos a data de hoje. Você pode ajustar se necessário."}
                </div>

                <CalendarInputV2
                  selectionMode="single"
                  setDates={setStartDate}
                  changeDates={setDateValue}
                  values={startDate}
                />

                <div className="mt-3" />

                <AppTextAreaV2
                  label="Observação (opcional)"
                  name="description"
                  errors={errors}
                  touched={touched}
                  value={values.description}
                  placeholder="Ex: Pago via Pix / Recebido em dinheiro / Observações..."
                  onChange={handleChange}
                />

                <div className="mt-3" />

                {isLoading ? (
                  <AppLoading />
                ) : (
                  <AppButton width="100%" type="submit" name="confirmar" />
                )}

                <div style={{ marginTop: 10 }}>
                  <AppLinkButton name="cancelar" onClick={closeShow} />
                </div>
              </Form>
            )}
          </Formik>
        </AppContainer>
      </Modal.Body>
    </Modal>
  );
}
